import {
  Combobox,
  ComboboxActionButton,
  ComboboxExtrasButton,
} from '@squareup/dex-ui';
import React, { FC, memo, useCallback } from 'react';

import { ApiExplorerFieldSpecialValuesDropdown } from '../helpers/ApiExplorerFieldSpecialValuesDropdown';
import { usePrimitiveProps } from '../hooks/nullable-hooks';
import { ApiExplorerPrimitiveFieldProps } from '../props';

const strings = {
  label: 'Generate',
  placeholder: {
    datetime: '2020-01-26T02:25:34Z',
    duration: 'P1W3D',
  },
};

// Have an array of examples to choose from when "generating"
const durationExamples = ['P1W4D', 'P2DT12H30M15S', 'P1Y2M3D', 'P2Y6M10DT5H'];

interface Props extends ApiExplorerPrimitiveFieldProps {}

const ApiExplorerDatetimeField: FC<Props> = memo(
  ({
    currentValue,
    onChange,
    path,
    name,
    actionButton,
    isNullable,
    datetimeFormat,
  }) => {
    const onInputChangeCallback = useCallback(
      (value: string) => {
        if (value === '') {
          onChange(undefined, path, 'remove');
        } else {
          onChange(value, path);
        }
      },
      [onChange, path]
    );

    const onGenerateButtonClicked = useCallback(() => {
      const datetime = new Date().toISOString();
      let randomIndex = Math.floor(Math.random() * durationExamples.length);

      if (currentValue === durationExamples[randomIndex]) {
        randomIndex = (randomIndex + 1) % durationExamples.length;
      }

      onChange(
        datetimeFormat === 'duration'
          ? durationExamples[randomIndex]
          : datetime,
        path
      );
    }, [currentValue, datetimeFormat, onChange, path]);

    const { placeholder, icon } = usePrimitiveProps(
      datetimeFormat === 'duration'
        ? strings.placeholder.duration
        : strings.placeholder.datetime,
      currentValue
    );

    return (
      <Combobox
        name={name}
        placeholder={placeholder}
        leadingAccessory={icon}
        value={currentValue?.toString()}
        onChange={onInputChangeCallback}
        actionButton={
          <>
            <ComboboxActionButton onClick={onGenerateButtonClicked}>
              {strings.label}
            </ComboboxActionButton>
            {actionButton}
          </>
        }
        extrasButton={
          <ApiExplorerFieldSpecialValuesDropdown
            currentValue={currentValue}
            showNullable={isNullable}
            showEmptyString={true}
            path={path}
            onChange={onChange}
            trigger={<ComboboxExtrasButton />}
          />
        }
      />
    );
  }
);

ApiExplorerDatetimeField.displayName = 'ApiExplorerDatetimeField';

export { ApiExplorerDatetimeField };
